import {
  ConfirmationDialog,
  DataGrid,
  formatDate,
  formatNumber,
  useNotifications
} from '@ijoin/components';
import {
  DbInsightsImportDataTable,
  getGetAdminDbInsightsImportDataQueryKey,
  getGetAdminDbInsightsTablesQueryKey,
  useGetAdminDbInsightsImportDataSuspense,
  usePostAdminDbInsightsImportDataPurge
} from '@ijoin/hooks';
import { Stack, Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { useQueryClient } from '@tanstack/react-query';

import { FC } from 'react';

import { errorMessage } from '../helpers';

export const ImportDataPurgeDialog: FC<{
  isOpen: boolean;
  onClose: () => void;
}> = props => {
  const queryClient = useQueryClient();
  const notifications = useNotifications();

  const importData = useGetAdminDbInsightsImportDataSuspense();
  const purge = usePostAdminDbInsightsImportDataPurge({
    mutation: {
      onError: err => {
        notifications.error(errorMessage(err, 'Failed to purge import data'));
        props.onClose();
      },
      onSuccess: result => {
        queryClient.invalidateQueries({
          queryKey: getGetAdminDbInsightsImportDataQueryKey()
        });
        queryClient.invalidateQueries({
          queryKey: getGetAdminDbInsightsTablesQueryKey()
        });
        notifications.success(
          `Purged ${formatNumber(result.data.reduce((total, row) => total + row.deleted, 0))} staging rows`
        );
        props.onClose();
      }
    }
  });

  const onConfirm = () => purge.mutate();

  const columns: GridColDef<DbInsightsImportDataTable>[] = [
    { field: 'table', flex: 2, headerName: 'Table' },
    {
      align: 'right',
      field: 'rows',
      headerAlign: 'right',
      headerName: 'Rows',
      valueFormatter: value => formatNumber(value),
      width: 100
    },
    {
      align: 'right',
      field: 'batches',
      headerAlign: 'right',
      headerName: 'Batches',
      valueFormatter: value => formatNumber(value),
      width: 100
    },
    {
      field: 'oldestImportAt',
      flex: 1,
      headerName: 'Oldest Import',
      valueFormatter: value =>
        value ? formatDate(value, { format: 'MM/DD/YYYY h:mm A' }) : ''
    }
  ];

  return (
    <ConfirmationDialog
      confirmationText='PURGE'
      confirmColor='error'
      confirmText='Purge'
      data-testid='purgeImportDataDialog'
      isOpen={props.isOpen}
      message={
        <Stack spacing={2}>
          <Typography>
            Deletes every row from the import staging tables below. Use this to
            clear rows a failed import left behind. It is refused while a plan
            or participant file import is in flight, and it cannot be undone.
          </Typography>
          <DataGrid
            columns={columns}
            disableColumnMenu
            disableRowSelectionOnClick
            getRowId={row => row.table}
            hideFooter
            rows={importData.data.data}
            slotProps={{
              root: {
                'data-component': 'dataGrid',
                'data-testid': 'importDataTables'
              }
            }}
          />
        </Stack>
      }
      onCancel={props.onClose}
      onConfirm={onConfirm}
      title='Purge Import Staging Data'
    />
  );
};

ImportDataPurgeDialog.displayName = 'ImportDataPurgeDialog';
